/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Button,
  Box,
} from '@mui/material'
import Dropzone from 'react-dropzone'
import moment from 'moment'
import { getDocs } from '../../../utils/fc'
import { currencyFormatter } from '../../../utils/currencyFormatter'
import { OIKOS_FILES_BASE } from '../../../constants/oikosReceitas'
import { notifyError, notifySuccess } from '../../../utils/notify'
import {
  buildInitialLibForm,
  digitsToBrlMask,
  filterLibDocs,
  formatBrlFromNumber,
  isRecAutorizado,
  num,
  parseBrlMaskedToNumber,
  submitLiberacaoRec,
} from '../../../utils/recLiberacaoFaturamento'

const dropStyle = {
  cursor: 'pointer',
  borderWidth: 2,
  borderRadius: 6,
  borderColor: '#ccc',
  borderStyle: 'dashed',
  backgroundColor: '#f8f8f8',
  outline: 'none',
  textAlign: 'center',
  padding: '10px 12px',
  fontSize: 12,
  color: '#666',
}

/**
 * Modal de liberação de faturamento (REC) — mesmo fluxo visual da guia Eventos de pagamento (`EvePag`).
 */
export default function ModalLiberacaoFaturamentoRec({ open, rec, onClose, onSuccess }) {
  const [recLibDocs, setRecLibDocs] = useState([])
  const [libForm, setLibForm] = useState({ dtLib: '', vlrLib: '', just: '', pdfFiles: [] })
  const [libSubmitting, setLibSubmitting] = useState(false)

  const setLibField = useCallback((field, value) => {
    setLibForm((s) => ({ ...s, [field]: value }))
  }, [])

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!open || !rec?.rec_id) return
      let docs = []
      try {
        const d = await getDocs('rec', rec.rec_id)
        docs = Array.isArray(d) ? d : []
      } catch (e) {
        console.log('getDocs rec', e)
      }
      if (cancelled) return
      setRecLibDocs(docs)
      setLibForm(buildInitialLibForm(rec))
    }
    load()
    return () => {
      cancelled = true
    }
  }, [open, rec])

  const onVlrLibChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '')
    const masked = digitsToBrlMask(digits)
    setLibField('vlrLib', masked)
  }

  const openDoc = (doc) => {
    const path = doc?.arquivo_caminho
    if (!path) return
    window.open(`${OIKOS_FILES_BASE}${path}`, '_blank')
  }

  const handleClose = () => {
    if (libSubmitting) return
    onClose()
  }

  const handleSubmit = async () => {
    if (!rec?.rec_id) return
    setLibSubmitting(true)
    try {
      await submitLiberacaoRec({ modalRecRow: rec, libForm, recLibDocs })
      notifySuccess('Liberação de faturamento registrada.')
      setLibSubmitting(false)
      if (onSuccess) await onSuccess()
      onClose()
    } catch (e) {
      console.log(e)
      notifyError(e?.message || 'Falha ao registrar liberação.')
      setLibSubmitting(false)
    }
  }

  if (!rec) return null

  const bruModal = num(rec.rec_vlrBru)
  const libModal = parseBrlMaskedToNumber(libForm.vlrLib)
  const needJustModal = Math.abs(bruModal - libModal) > 0.01
  const libDocsInModal = filterLibDocs(recLibDocs)
  const maxDtLib = moment().format('YYYY-MM-DD')
  const locked = isRecAutorizado(rec)
  const showJustField = needJustModal || (locked && String(rec.rec_justDifLibAutNf || '').trim())

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Liberação de faturamento</DialogTitle>
      <DialogContent dividers>
        {locked && (
          <Typography variant="body2" sx={{ mb: 2, p: 1.5, bgcolor: 'action.selected', borderRadius: 1 }}>
            Faturamento já autorizado. Os dados abaixo não podem ser alterados.
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          EP: <strong>{rec.rec_ep || '—'}</strong>
          {' · '}
          Valor bruto previsto: <strong>{currencyFormatter.format(num(rec.rec_vlrBru))}</strong>
        </Typography>

        {libDocsInModal.length > 0 && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              PDF(s) de liberação já enviados
            </Typography>
            {libDocsInModal.map((doc) => (
              <Box key={doc.arquivo_id || doc.documento_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap title={doc.arquivo_desc || doc.documento_desc}>
                  {doc.arquivo_desc || doc.documento_desc || 'PDF'}
                </Typography>
                <Button size="small" variant="outlined" onClick={() => openDoc(doc)}>
                  Abrir PDF
                </Button>
              </Box>
            ))}
          </Box>
        )}

        <TextField
          margin="dense"
          label="Data da liberação"
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: maxDtLib }}
          value={libForm.dtLib}
          onChange={(e) => setLibField('dtLib', e.target.value)}
          disabled={libSubmitting || locked}
        />
        <TextField
          margin="dense"
          label="Valor liberado"
          fullWidth
          value={libForm.vlrLib}
          onChange={onVlrLibChange}
          disabled={libSubmitting || locked}
          placeholder="0,00"
          inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
          helperText={!locked ? 'Formato: milhares com ponto, centavos após vírgula (ex.: 12.345,67)' : undefined}
        />
        {showJustField && (
          <TextField
            margin="dense"
            label="Justificativa (valor liberado diferente do bruto previsto)"
            fullWidth
            multiline
            minRows={3}
            value={locked ? (rec.rec_justDifLibAutNf || '') : libForm.just}
            onChange={(e) => setLibField('just', e.target.value)}
            disabled={libSubmitting || locked}
            required={!locked && needJustModal}
          />
        )}
        {!locked && (
          <>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, mb: 0.5 }}>
              {libDocsInModal.length > 0
                ? 'Adicionar mais PDF(s) de autorização (opcional ao salvar alterações nos dados acima)'
                : 'PDF(s) de autorização para emissão da NF (obrigatório na primeira vez)'}
            </Typography>
            <Dropzone
              multiple
              accept={['application/pdf', '.pdf']}
              onDrop={(accepted) => {
                const next = [...(libForm.pdfFiles || []), ...(accepted || [])]
                setLibField('pdfFiles', next)
              }}
              disabled={libSubmitting}
            >
              {({ getRootProps, getInputProps, isDragActive, isDragReject }) => (
                <div
                  {...getRootProps()}
                  style={{
                    ...dropStyle,
                    borderColor: isDragReject ? 'red' : isDragActive ? '#4aa3ff' : '#ccc',
                  }}
                >
                  <input {...getInputProps()} />
                  Arraste um ou mais PDFs ou clique para selecionar
                </div>
              )}
            </Dropzone>
            {(libForm.pdfFiles || []).length > 0 && (
              <Box sx={{ mt: 1 }}>
                {(libForm.pdfFiles || []).map((f, idx) => (
                  <Typography key={`${f.name}-${idx}`} variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</span>
                    <Button
                      size="small"
                      onClick={() => {
                        const rest = (libForm.pdfFiles || []).filter((_, i) => i !== idx)
                        setLibField('pdfFiles', rest)
                      }}
                      disabled={libSubmitting}
                    >
                      Remover
                    </Button>
                  </Typography>
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {locked ? (
          <Button onClick={handleClose} variant="contained">
            Fechar
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={libSubmitting}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={libSubmitting}>
              {libSubmitting ? 'Salvando…' : 'Autorizar faturamento'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
